<script setup lang="ts">
import type { Placement } from '@/types/ui';
/**
 * UiTooltip - Design System Tooltip Component
 *
 * A small popup that appears on hover or focus to provide brief, supplementary
 * information. Tooltips help users understand UI elements without cluttering the
 * interface.
 *
 * WHAT IT IS:
 * A floating label that appears when you hover over or focus on an element.
 * It provides extra context or explanation that doesn't fit in the UI.
 *
 * WHEN TO USE:
 * - Explain icon-only buttons (e.g., tooltip "Delete" on a trash icon)
 * - Show full text for truncated content
 * - Provide keyboard shortcuts (e.g., "Save (Ctrl+S)")
 * - Give brief help text for form fields
 * - Explain disabled states (e.g., "Login required")
 *
 * The floating element is rendered into <body> via <Teleport> so it escapes any
 * ancestor `overflow: hidden | auto | scroll` clipping (cards, scroll regions,
 * modals). Positioning uses @floating-ui/vue with flip/shift middleware so the
 * tooltip stays on-screen near container and viewport edges instead of being cut off.
 *
 * @example
 * <UiTooltip content="Delete item">
 *   <UiButton square><Icon icon="trash" /></UiButton>
 * </UiTooltip>
 */
import {
   arrow,
   autoUpdate,
   flip,
   offset,
   shift,
   useFloating,
} from '@floating-ui/vue';
import { computed, onUnmounted, ref, toRef, useAttrs, useSlots } from 'vue';

export interface UiTooltipProps {
   /** Tooltip text content */
   content?: string;
   /** Placement relative to trigger (may flip to fit the viewport) */
   placement?: Placement;
   /** Delay before showing (ms) */
   showDelay?: number;
   /** Delay before hiding (ms) */
   hideDelay?: number;
   /** Disable the tooltip */
   disabled?: boolean;
   /** Allow wrapping with a capped max width. Default is single-line nowrap. */
   multiline?: boolean;
   /** Inject <wbr> hints after /, _, - so the bubble prefers those break points. Requires multiline. */
   breakHints?: boolean;
}

// Forward consumer-supplied `class` to the trigger wrapper instead of letting it
// land ambiguously across the component's two roots (trigger + teleport).
defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<UiTooltipProps>(), {
   placement: 'top',
   showDelay: 200,
   hideDelay: 0,
   disabled: false,
   multiline: false,
   breakHints: false,
});

const attrs = useAttrs();
const slots = useSlots();
const hasContent = computed(() => Boolean(props.content) || Boolean(slots.content));

const isVisible = ref(false);
let showTimeout: ReturnType<typeof setTimeout> | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;

// Refs for floating-ui positioning. The trigger wrapper is the reference element;
// the teleported tooltip is the floating element.
const triggerRef = ref<HTMLElement | null>(null);
const floatingRef = ref<HTMLElement | null>(null);
const arrowRef = ref<HTMLElement | null>(null);

const ARROW_SIZE = 8; // px; matches the w-2/h-2 diamond below

const { floatingStyles, placement: resolvedPlacement, middlewareData } = useFloating(
   triggerRef,
   floatingRef,
   {
      placement: toRef(props, 'placement'),
      open: isVisible,
      // Reposition on scroll/resize/layout changes while the tooltip is mounted.
      whileElementsMounted: autoUpdate,
      // Use top/left positioning (not transform) so the enter/leave scale
      // transition below doesn't fight floating-ui's transform.
      transform: false,
      middleware: [
         offset(ARROW_SIZE),
         flip({ padding: 8 }),
         shift({ padding: 8 }),
         arrow({ element: arrowRef }),
      ],
   },
);

// Wrap long content only when opted in; otherwise keep a single line.
const bubbleWhitespaceClasses = computed(() =>
   props.multiline
      ? 'whitespace-normal break-words max-w-xs'
      : 'whitespace-nowrap',
);

function escapeHtml(input: string): string {
   return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
}

// Escape first, then inject <wbr> after path separators so the bubble prefers
// breaking at natural boundaries. v-html only ever sees the already-escaped string.
const hintedContent = computed(() => {
   if (!props.breakHints || !props.content)
      return '';
   return escapeHtml(props.content).replace(/([/_\-])/g, '$1<wbr>');
});

// Side of the floating element the arrow points toward, derived from the
// placement floating-ui actually resolved to (after any flip).
const arrowStyles = computed(() => {
   const side = resolvedPlacement.value.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';
   const staticSide = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
   }[side];
   const { x, y } = middlewareData.value.arrow ?? {};
   return {
      left: x != null ? `${x}px` : '',
      top: y != null ? `${y}px` : '',
      [staticSide]: `${-ARROW_SIZE / 2}px`,
   };
});

function show() {
   if (props.disabled)
      return;

   if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
   }

   showTimeout = setTimeout(() => {
      // Re-check disabled in case the caller updates it asynchronously
      // (e.g. measuring truncation on mouseenter before deciding to show).
      if (props.disabled)
         return;
      isVisible.value = true;
   }, props.showDelay);
}

function hide() {
   if (showTimeout) {
      clearTimeout(showTimeout);
      showTimeout = null;
   }

   hideTimeout = setTimeout(() => {
      isVisible.value = false;
   }, props.hideDelay);
}

// Clear pending timers on unmount to prevent callbacks after component is destroyed
onUnmounted(() => {
   if (showTimeout) {
      clearTimeout(showTimeout);
      showTimeout = null;
   }
   if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
   }
});
</script>

<template>
   <span
      ref="triggerRef"
      class="inline-flex"
      :class="attrs.class"
      @mouseenter="show"
      @mouseleave="hide"
      @focusin="show"
      @focusout="hide"
   >
      <!-- Trigger element -->
      <slot />
   </span>

   <!-- Tooltip rendered into <body> so it escapes ancestor overflow clipping -->
   <Teleport to="body">
      <Transition
         enter-active-class="transition duration-150 ease-out"
         enter-from-class="opacity-0 scale-95"
         enter-to-class="opacity-100 scale-100"
         leave-active-class="transition duration-100 ease-in"
         leave-from-class="opacity-100 scale-100"
         leave-to-class="opacity-0 scale-95"
      >
         <div
            v-if="isVisible && hasContent"
            ref="floatingRef"
            role="tooltip"
            class="z-50 px-3 py-1.5 text-xs text-white bg-neutral-darker rounded-lg pointer-events-none"
            :class="bubbleWhitespaceClasses"
            :style="floatingStyles"
         >
            <!-- eslint-disable-next-line vue/no-v-html (content is HTML-escaped in hintedContent) -->
            <span v-if="breakHints && content && !slots.content" v-html="hintedContent" />
            <slot v-else name="content">
               {{ content }}
            </slot>
            <!-- Arrow -->
            <span
               ref="arrowRef"
               class="absolute w-2 h-2 bg-neutral-darker rotate-45"
               :style="arrowStyles"
            />
         </div>
      </Transition>
   </Teleport>
</template>
